/**
 * One rung of a SKU's retry ladder.
 *
 * A rung answers three things and keeps them apart:
 *
 *   1. WHERE it lands — `channelSlug / upstreamModelName`.
 *   2. WHAT WAS DECLARED — 首发 N% / 纯兜底 / 已关闭.
 *   3. WHAT IT ACTUALLY CARRIED for this SKU, and how much of that failed.
 *
 * ── The bar, and why all three live on one axis ──
 *
 * Every rung of a SKU draws on the SAME axis, where the full width is 100% of
 * that SKU's traffic. So:
 *
 *   BAR LENGTH  = this leg's measured share of the SKU's DIALS (`shareOfSku`,
 *                 dialled volume — tenant-final rows plus the attempt
 *                 failures the ladder moved past, #2240)
 *   BAR SPLIT   = served vs failed WITHIN that leg
 *   TICK        = the declared first-attempt share, on the same axis
 *
 * Which makes the page's whole reason to exist a single visual comparison
 * rather than two numbers in different columns. Production 2026-09-01,
 * `openai/gpt-image-2`:
 *
 *   ucloud-images   126 req (48.8%)   18 failed (14.3%)   tick at 100%
 *   llmport-img     120 req (46.5%)  120 failed (100.0%)  tick at 0%
 *   kkhub-images     12 req ( 4.7%)   12 failed (100.0%)  tick at 0%
 *
 * The second row is a bar reaching nearly half the axis, entirely red, with
 * its tick pinned at the origin: it took 46.5% of a SKU it is configured to
 * take none of, and failed all of it. That reads in one glance and no number
 * on the old rung said it — the old rung showed the CHANNEL's 0.8% rate,
 * which is a different quantity about a different subject, and it said 正常.
 *
 * ── Why the bar has THREE segments ──
 *
 * Because red has to mean "this leg failed it". A leg row keeps the channel
 * that was SELECTED, and selection happens before the admission gate, so a
 * leg carries requests it never dialled for. `claude-code/claude-fable-5`'s
 * busiest leg is 9,055 carried, 7,097 of them refused before any dial, and 28
 * genuine failures. Splitting only served-vs-failed painted that bar 78.7%
 * red for a leg whose real failure count was 28.
 *
 * So: served / refused-before-dial (neutral) / failed (net). Length still
 * means total volume carried — the leg really did carry those requests — but
 * the refusals sit in their own neutral segment instead of being folded into
 * either success or failure, both of which would be a claim.
 *
 * ── Absence is not health ──
 *
 * A configured rung with no leg renders 无流量 / "no traffic" — it is a real
 * finding (a first-attempt target receiving nothing is a broken ladder) and it
 * must never render as a healthy zero, nor fall back to the channel figure.
 * When the gateway cannot report legs at all, the rung says so instead:
 * `SupplyLink.measured` is null in both cases and only
 * `PerspectiveModel.skuChannelsReported` tells them apart.
 */

import { useT } from '../../i18n/index.ts';
import type { I18nKey } from '../../i18n/index.ts';
import { Chip, Td, Tr } from '../../ui/index.ts';
import {
  endpointHasChannel,
  endpointLead,
  formatInteger,
  formatPercentPoints,
  formatRate,
} from './display.ts';
import { VERDICT_TEXT_CLASS, legDisplayFigures, legDisplayRate, legRateIsLowerBound, legVerdict } from './supplyVerdict.ts';
import type { SupplyVerdict } from './supplyVerdict.ts';
import type { MeasuredLeg, SupplyLink, UpstreamModelNode } from './types.ts';

/** Ladder axis geometry, in SVG user units. The full track is 100% of the SKU. */
const TRACK = 132;
const BAR_HEIGHT = 9;
const SVG_HEIGHT = 13;
const TICK_TOP = -1;
const TICK_BOTTOM = BAR_HEIGHT + 1;
/**
 * A measured leg that carried anything is never invisible, and a tick at 0%
 * still has to be findable — both floors exist so "too small to see" cannot be
 * confused with "nothing".
 */
export const LADDER_MIN_BAR = 3;
const TICK_MIN_X = 1;

const ROLE_LABEL_KEY: Readonly<Record<'reserve' | 'disabled', I18nKey>> = {
  reserve: 'health_perspective.table.rung.reserve',
  disabled: 'health_perspective.table.rung.disabled',
};

interface Props {
  readonly link: SupplyLink;
  readonly model: UpstreamModelNode | null;
  readonly columnCount: number;
  /** `PerspectiveModel.skuChannelsReported` — decides what a null leg means. */
  readonly legsReported: boolean;
  readonly selectedNodeId: string | null;
  readonly onSelectNode: (nodeId: string) => void;
}

export function LadderRow({
  link,
  model,
  columnCount,
  legsReported,
  selectedNodeId,
  onSelectNode,
}: Props): JSX.Element {
  const t = useT();
  // The link denormalises the endpoint so a rung renders without a lookup;
  // the node is the fallback for the fields the link does not carry.
  const channelSlug = link.channelSlug ?? model?.channelSlug ?? null;
  const modelName = link.upstreamModelName ?? model?.upstreamModelName ?? null;
  const capabilityStatus = link.capabilityStatus ?? model?.capabilityStatus ?? null;
  const leg = link.measured;

  return (
    <Tr
      className={model !== null && selectedNodeId === model.id ? 'bg-active' : undefined}
      data-testid={`health-perspective-rung-${link.id}`}
    >
      <Td className="pl-10">
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <LadderBar link={link} leg={leg} />
            <RoleChip link={link} />
            <EndpointLabel
              upstreamName={link.upstreamName ?? model?.upstreamName ?? null}
              channelSlug={channelSlug}
              modelName={modelName}
              onSelect={model === null ? null : () => onSelectNode(model.id)}
            />
          </div>
          <div className="flex flex-wrap gap-x-3 gap-y-0.5 font-mono text-micro text-paper-low">
            {leg !== null && legRateIsLowerBound(leg) ? (
              <span
                className="text-warn"
                data-testid={`health-perspective-rung-bound-${link.id}`}
              >
                {t('health_perspective.refusal.unknown_short')}
              </span>
            ) : null}
            <span>{t('health_perspective.inspector.chain.priority', { value: link.priority })}</span>
            {capabilityStatus === null ? null : (
              <span data-testid={`health-perspective-rung-capability-${link.id}`}>
                {t('health_perspective.inspector.chain.capability', { status: capabilityStatus })}
              </span>
            )}
          </div>
        </div>
      </Td>
      <Td num colSpan={columnCount - 1} className="align-top text-micro">
        <LegFigures link={link} leg={leg} legsReported={legsReported} />
      </Td>
    </Tr>
  );
}

/**
 * The numbers, beside the shape: the shape is for scanning a ladder, these
 * are for acting on one rung.
 *
 * The three states of a null leg are three different sentences. Rendering any
 * of them as 0 failures would be the page's original sin in miniature.
 */
function LegFigures({
  link,
  leg,
  legsReported,
}: {
  readonly link: SupplyLink;
  readonly leg: MeasuredLeg | null;
  readonly legsReported: boolean;
}): JSX.Element {
  const t = useT();
  if (!legsReported) {
    return (
      <span className="text-warn" data-testid={`health-perspective-rung-unknown-${link.id}`}>
        {t('health_perspective.table.rung.legs_unavailable')}
      </span>
    );
  }
  if (leg === null || leg.dialled === 0) {
    return (
      <span className="text-paper-low" data-testid={`health-perspective-rung-idle-${link.id}`}>
        {t('health_perspective.table.rung.no_traffic')}
      </span>
    );
  }
  const failed = legDisplayFigures(leg);
  return (
    <span className="inline-flex flex-wrap justify-end gap-x-3 gap-y-0.5">
      {/* DIALLED volume: what this rung was actually tried for, rescued
          429s included, so a primary leg the fallback keeps bailing out
          still reads as the leg that carried the SKU's first attempts. */}
      <span data-testid={`health-perspective-rung-volume-${link.id}`}>
        {t('health_perspective.table.rung.volume', {
          requests: formatInteger(leg.dialled),
          share: formatRate(leg.shareOfSku),
        })}
      </span>
      {/* The dials that failed and were moved past — the tenant never saw
          them, so they are in no other figure on this row. */}
      {leg.upstreamAttemptFailures === null || leg.upstreamAttemptFailures === 0 ? null : (
        <span
          className={VERDICT_TEXT_CLASS[legVerdict(leg)]}
          data-testid={`health-perspective-rung-attempts-${link.id}`}
        >
          {t('health_perspective.table.rung.attempt_failed', {
            attempts: formatInteger(leg.upstreamAttemptFailures),
            rateLimited: formatInteger(leg.upstreamAttemptRateLimited ?? 0),
          })}
        </span>
      )}
      {/* Named, not folded away: these are requests this leg was chosen for
          and never dialled for. They are neither its successes nor its
          failures, and the count is what makes the other two legible. */}
      {leg.refusedBeforeUpstream === null || leg.refusedBeforeUpstream === 0 ? null : (
        <span data-testid={`health-perspective-rung-refused-${link.id}`}>
          {t('health_perspective.table.rung.refused', {
            refused: formatInteger(leg.refusedBeforeUpstream),
          })}
        </span>
      )}
      {failed === null ? (
        <span className="text-warn" data-testid={`health-perspective-rung-failed-unknown-${link.id}`}>
          {t('health_perspective.table.rung.failed_unknown')}
        </span>
      ) : (
        <span
          className={VERDICT_TEXT_CLASS[legVerdict(leg)]}
          data-testid={`health-perspective-rung-failed-${link.id}`}
        >
          {/* "At least", when this grain cannot tell an excusable refusal
              from one of ours: `skuChannels` carries the refusal scalar but
              no code split, so deducting all of it over-excludes and the
              printed rate is a floor. `legVerdict` refuses to call such a leg
              healthy on the strength of the flattering bound. */}
          {t(
            legRateIsLowerBound(leg)
              ? 'health_perspective.table.rung.failed_at_least'
              : 'health_perspective.table.rung.failed',
            {
              // Count and rate from ONE arm — see `legDisplayFigures`. With
              // the supply counters this is extra failed dials + failed
              // final dials; the 重试失败 line above is its breakdown.
              errors: formatInteger(failed.count),
              rate: formatRate(failed.rate),
            },
          )}
        </span>
      )}
    </span>
  );
}



const VERDICT_FILL: Readonly<Record<SupplyVerdict, string>> = {
  ok: 'fill-ok',
  warn: 'fill-warn',
  bad: 'fill-bad',
  no_traffic: 'fill-line-strong',
  unknown: 'fill-line-strong',
};

/**
 * Measured share as length, served-vs-failed as the split, declared share as a
 * tick — all on one axis whose full width is 100% of this SKU's traffic.
 *
 * The axis is per-SKU and linear: `shareOfSku` is already a fraction of a
 * fixed whole, so nothing is compressed and there is no scale to disclose
 * (unlike `OutcomeBar`, which ranks SKUs against each other on sqrt).
 */
function LadderBar({
  link,
  leg,
}: {
  readonly link: SupplyLink;
  readonly leg: MeasuredLeg | null;
}): JSX.Element {
  const t = useT();
  const configuredX = Math.max(
    TICK_MIN_X,
    (TRACK * Math.min(100, Math.max(0, link.configuredSharePct))) / 100,
  );
  const carried = leg === null || leg.dialled === 0 ? 0 : Math.min(1, leg.shareOfSku);
  const barWidth = carried > 0 ? Math.max(LADDER_MIN_BAR, TRACK * carried) : 0;
  const verdict = leg === null ? 'no_traffic' : legVerdict(leg);
  const gradable = leg !== null && leg.failureRate !== null && leg.attributableErrors !== null;

  // Refusals first, then what the leg actually served, then what it actually
  // failed. Ungradable legs draw one neutral block: a red or green split
  // would state a verdict the data cannot support.
  // Over DIALLED volume, which the bar's length is. The red segment is what
  // failed on this leg by either arm: net failures plus the dials the ladder
  // moved past (#2240), which are failures the tenant never saw.
  const refusedWidth =
    gradable && leg !== null && (leg.refusedBeforeUpstream ?? 0) > 0
      ? Math.min(barWidth, Math.max(LADDER_MIN_BAR / 2, barWidth * ((leg.refusedBeforeUpstream ?? 0) / leg.dialled)))
      : 0;
  const failedOnLeg = leg === null ? 0 : (leg.attributableErrors ?? 0) + (leg.upstreamAttemptFailures ?? 0);
  const failedWidth =
    gradable && leg !== null && failedOnLeg > 0
      ? Math.min(
          barWidth - refusedWidth,
          Math.max(LADDER_MIN_BAR / 2, barWidth * (failedOnLeg / leg.dialled)),
        )
      : 0;
  const servedWidth = Math.max(0, barWidth - refusedWidth - failedWidth);

  const label =
    leg === null || leg.dialled === 0
      ? t('health_perspective.table.rung.no_traffic')
      : gradable
        ? t('health_perspective.table.rung.bar_aria', {
            share: formatRate(leg.shareOfSku),
            requests: formatInteger(leg.dialled),
            refused: formatInteger(leg.refusedBeforeUpstream ?? 0),
            errors: formatInteger(failedOnLeg),
            rate: formatRate(legDisplayRate(leg)),
            configured: formatPercentPoints(link.configuredSharePct),
          })
        : t('health_perspective.table.rung.bar_aria_unknown', {
            share: formatRate(leg.shareOfSku),
            requests: formatInteger(leg.dialled),
            configured: formatPercentPoints(link.configuredSharePct),
          });

  return (
    <svg role="img" aria-label={label} width={TRACK} height={SVG_HEIGHT} className="shrink-0">
      {/* The axis: the whole of this SKU's traffic. */}
      <rect x={0} y={0} width={TRACK} height={BAR_HEIGHT} rx={1} className="fill-well" />
      {barWidth > 0 ? (
        <g data-testid={`health-perspective-rung-bar-${link.id}`} data-width={barWidth.toFixed(2)}>
          <rect
            x={0}
            y={0}
            width={servedWidth}
            height={BAR_HEIGHT}
            rx={1}
            className={gradable ? 'fill-ok' : 'fill-line-strong'}
          />
          {refusedWidth > 0 ? (
            <rect
              x={servedWidth}
              y={0}
              width={refusedWidth}
              height={BAR_HEIGHT}
              className="fill-paper-mid"
              data-testid={`health-perspective-rung-bar-refused-${link.id}`}
            />
          ) : null}
          {failedWidth > 0 ? (
            <rect
              x={servedWidth + refusedWidth}
              y={0}
              width={failedWidth}
              height={BAR_HEIGHT}
              rx={1}
              className={VERDICT_FILL[verdict]}
              data-testid={`health-perspective-rung-bar-failed-${link.id}`}
            />
          ) : null}
        </g>
      ) : null}
      {/* Declared share, on the same axis. A `reserve` rung ticks at the
          origin — that is not a decoration, it is the claim "this should be
          taking none", and the gap to the bar's end is the divergence. */}
      <line
        x1={configuredX}
        x2={configuredX}
        y1={TICK_TOP}
        y2={TICK_BOTTOM}
        strokeWidth={1.5}
        className="stroke-signal-bright"
        data-testid={`health-perspective-rung-tick-${link.id}`}
        data-x={configuredX.toFixed(2)}
      />
    </svg>
  );
}

/**
 * The role chip.
 *
 * `active` spells the declared first-attempt share into the chip itself
 * (首发 100%) because the share only means anything for an active rung: it is
 * 0 BY DEFINITION for reserve and disabled, and printing "0%" beside them
 * would make a pure-fallback target look like a first-attempt target that
 * happens to be starved. The other two roles carry no number at all.
 */
function RoleChip({ link }: { readonly link: SupplyLink }): JSX.Element {
  const t = useT();
  if (link.role === 'active') {
    return (
      <Chip tone="signal" lamp={false} testId={`health-perspective-role-${link.id}`}>
        <span data-testid={`health-perspective-rung-share-${link.id}`}>
          {t('health_perspective.table.rung.active', {
            pct: formatPercentPoints(link.configuredSharePct),
          })}
        </span>
      </Chip>
    );
  }
  return (
    <Chip tone="neutral" lamp={false} testId={`health-perspective-role-${link.id}`}>
      {t(ROLE_LABEL_KEY[link.role])}
    </Chip>
  );
}

/**
 * `dragoncode · anthropic_oauth / claude-fable-5`.
 *
 * The SUPPLIER leads and carries the weight; the channel follows in
 * `paper-low` because it is still what identifies which lane of that supplier
 * — several channels share one, and which one a SKU fell back onto matters.
 * With no name the channel slug leads on its own rather than leaving a blank
 * or printing a raw id.
 */
function EndpointLabel({
  upstreamName,
  channelSlug,
  modelName,
  onSelect,
}: {
  readonly upstreamName: string | null;
  readonly channelSlug: string | null;
  readonly modelName: string | null;
  readonly onSelect: (() => void) | null;
}): JSX.Element {
  const t = useT();
  const parts = { upstreamName, channelSlug };
  const body = (
    <>
      <Part value={endpointLead(parts)} placeholder={t('health_perspective.table.endpoint_unknown')} />
      {endpointHasChannel(parts) ? (
        <span className="text-paper-low"> · {channelSlug}</span>
      ) : null}
      <span className="text-paper-low"> / </span>
      <Part value={modelName} placeholder={t('health_perspective.table.endpoint_unknown')} />
    </>
  );
  if (onSelect === null) {
    return <span className="min-w-0 truncate font-mono text-dense">{body}</span>;
  }
  return (
    <button
      type="button"
      onClick={onSelect}
      className="min-w-0 truncate text-left font-mono text-dense transition-colors duration-120 hover:text-signal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/40"
    >
      {body}
    </button>
  );
}

function Part({
  value,
  placeholder,
}: {
  readonly value: string | null;
  readonly placeholder: string;
}): JSX.Element {
  if (value === null || value.length === 0) {
    return <span className="text-paper-faint">{placeholder}</span>;
  }
  return <span className="text-paper">{value}</span>;
}
